import type React from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface ScrollLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const ScrollLink = ({ to, children, className, onClick }: ScrollLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    const hash = to.includes("#") ? to.split("#")[1] : "";
    const targetPath = to.split("#")[0] || "/";

    const scrollToHash = (id: string) => {
      if (!id) return;

      const start = performance.now();
      const maxMs = 2500;

      const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (performance.now() - start < maxMs) requestAnimationFrame(attempt);
      };

      requestAnimationFrame(attempt);
    };

    const currentPath = location.pathname === "" ? "/" : location.pathname;
    const normalizedTarget = targetPath === "" ? "/" : targetPath;
    const isSamePage = currentPath === normalizedTarget || (currentPath === "/" && normalizedTarget === "/");

    if (isSamePage) {
      if (hash && typeof window !== "undefined") {
        window.history.pushState(null, "", `${targetPath}#${hash}`);
      }
      scrollToHash(hash);
    } else {
      navigate(`${targetPath}${hash ? `#${hash}` : ""}`);
      scrollToHash(hash);
    }

    onClick?.();
  };

  const href = to;

  return (
    <a href={href} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

export default ScrollLink;
